import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { formatDateTime } from '@euler/utils/formatters';
import { useCallback, useRef } from 'react';

const DateTimePicker = (props: {
  value?: Date;
  minimumDate?: Date;
  mode?: 'time' | 'date' | 'datetime' | undefined;
  onChange?: (e: any, newDate: Date | undefined) => void;
  onCancel?: () => void;
}) => {
  const { rpx } = useResponsiveContext();
  const ref = useRef<HTMLInputElement>(null);
  // the native picker indicator never receives its activating event inside the
  // gesture-handler tree (bottom sheets / scroll views set `touch-action: none`),
  // so open the picker ourselves on click.
  const openPicker = useCallback(() => {
    const input = ref.current;
    if (!input || typeof input.showPicker !== 'function') return;
    try {
      input.showPicker();
    } catch (e) {
      // showPicker() throws without transient user activation or when the
      // browser has no picker for this input type - the native indicator
      // remains as the fallback.
    }
  }, []);
  return (
    <input
      ref={ref}
      onClick={openPicker}
      value={
        props.value
          ? formatDateTime(
              props.value,
              props.mode === 'date' ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm:ss',
            )
          : ''
      }
      onChange={e =>
        props.onChange?.(
          { type: 'set', nativeEvent: { timestamp: Date.now() } },
          e.currentTarget.value ? new Date(e.currentTarget.value) : undefined,
        )
      }
      min={
        props.minimumDate
          ? formatDateTime(
              props.minimumDate,
              props.mode === 'date' ? 'yyyy-MM-dd' : "yyyy-MM-dd'T'HH:mm",
            )
          : undefined
      }
      type={
        props.mode === 'date'
          ? 'date'
          : props.mode === 'time'
          ? 'time'
          : 'datetime-local'
      }
      style={{
        padding: rpx(10),
        borderWidth: 1,
        borderRadius: rpx(5),
        marginTop: rpx(15),
        marginBottom: rpx(15),
        opacity: 1,
      }}
    />
  );
};

export default DateTimePicker;
